import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { formatDateHeading, formatWindowTime, isWindowEndingAtMidnight } from "./helpers";

function AvailabilityWindowsSection({ deleteWindow, editWindow, fields = [], intl, openWindowModal, windows }) {
  const groups = useMemo(() => groupAvailabilityWindows(windows), [windows]);
  const clubSummaries = useMemo(() => summarizeClubs(windows, fields), [windows, fields]);
  const dateCount = new Set(windows.map((windowItem) => windowItem.date.slice(0, 10))).size;
  const courtCount = getAvailableCourtIds(windows, fields).size;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) 340px" },
        gridTemplateAreas: { xs: '"summary" "windows"', xl: '"windows summary"' },
        gap: 2,
        alignItems: "start",
      }}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0, gridArea: "windows" }}>
        {windows.length === 0 ? (
          <Alert severity="warning">{intl.formatMessage({ id: "scheduling.availability.empty" })}</Alert>
        ) : (
          <>
            <Alert severity="info">
              {intl.formatMessage({ id: "scheduling.availability.persistenceHelp" })}
            </Alert>
            {groups.map((group) => (
              <AvailabilityGroupCard
                key={group.key}
                group={group}
                intl={intl}
                editWindow={editWindow}
                deleteWindow={deleteWindow}
              />
            ))}
          </>
        )}
      </Stack>
      <AvailabilitySummary
        clubs={clubSummaries}
        courtCount={courtCount}
        dateCount={dateCount}
        intl={intl}
        onAdd={openWindowModal}
        windowCount={windows.length}
      />
    </Box>
  );
}

function AvailabilityGroupCard({ group, intl, editWindow, deleteWindow }) {
  const first = group.windows[0];
  const clubName = getClubName(first);
  const courtName = getCourtName(first, intl);
  const endsAtMidnight = isWindowEndingAtMidnight(first.start_time, first.end_time);
  const end = formatWindowTime(first.end_time, { treatMidnightAsEndOfDay: endsAtMidnight });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <BusinessIcon color="primary" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>
                {clubName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {courtName}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
            <ScheduleIcon color="action" fontSize="small" />
            <Typography variant="subtitle2">
              {formatWindowTime(first.start_time)} → {end}
            </Typography>
            {endsAtMidnight && (
              <Chip
                size="small"
                variant="outlined"
                label={intl.formatMessage({ id: "scheduling.availability.endsAtMidnight" })}
              />
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">
          {intl.formatMessage({ id: "scheduling.availability.availableDates" })}
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
          {group.windows.map((windowItem) => {
            const date = windowItem.date.slice(0, 10);
            const dateLabel = formatDateHeading(date, intl);

            return (
              <Stack
                key={windowItem.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ px: 1, py: 0.5, bgcolor: "background.base", borderRadius: 1 }}
              >
                <CalendarMonthIcon color="action" fontSize="small" />
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {dateLabel}
                </Typography>
                <Tooltip title={intl.formatMessage({ id: "scheduling.availability.editOne" })}>
                  <IconButton
                    size="small"
                    aria-label={`${intl.formatMessage({ id: "Editar" })} ${dateLabel}`}
                    onClick={() => editWindow(windowItem)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={intl.formatMessage({ id: "scheduling.availability.deleteOne" })}>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`${intl.formatMessage({ id: "Eliminar" })} ${dateLabel}`}
                    onClick={() => deleteWindow(windowItem.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

function AvailabilitySummary({ clubs, courtCount, dateCount, intl, onAdd, windowCount }) {
  return (
    <Card
      variant="outlined"
      sx={{ alignSelf: "start", gridArea: "summary", position: { xl: "sticky" }, top: { xl: 16 } }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="overline" color="primary.main">
              {intl.formatMessage({ id: "scheduling.availability.summaryEyebrow" })}
            </Typography>
            <Typography variant="h6">
              {intl.formatMessage({ id: "scheduling.availability.summaryTitle" })}
            </Typography>
          </Box>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={intl.formatMessage({ id: "scheduling.availability.required" })}
          />
        </Stack>
        <Button fullWidth variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={onAdd} sx={{ my: 2 }}>
          {intl.formatMessage({ id: "scheduling.availability.add" })}
        </Button>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
          <SummaryMetric value={windowCount} label={intl.formatMessage({ id: "scheduling.availability.windows" })} />
          <SummaryMetric value={clubs.length} label={intl.formatMessage({ id: "scheduling.availability.clubs" })} />
          <SummaryMetric value={courtCount} label={intl.formatMessage({ id: "scheduling.availability.courtsAvailable" })} />
          <SummaryMetric value={dateCount} label={intl.formatMessage({ id: "scheduling.availability.dates" })} />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          {intl.formatMessage({ id: "scheduling.availability.clubBreakdown" })}
        </Typography>
        <Stack
          spacing={1.5}
          divider={<Divider flexItem />}
          sx={{ maxHeight: { xl: 420 }, overflowY: { xl: "auto" }, pr: { xl: 0.5 } }}
        >
          {clubs.map((club) => (
            <Box key={club.key}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle2">{club.name}</Typography>
                <Chip size="small" color="success" variant="outlined" label={intl.formatMessage({ id: "Activo" })} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage(
                  { id: "scheduling.availability.clubSummary" },
                  { windows: club.windowCount, courts: club.courtCount, dates: club.dateCount },
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {club.allCourts
                  ? intl.formatMessage({ id: "scheduling.availability.allCourtsIncluded" })
                  : intl.formatMessage(
                      { id: "scheduling.availability.specificCourts" },
                      { count: club.specificCourtCount },
                    )}
              </Typography>
            </Box>
          ))}
          {clubs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: "scheduling.availability.noClubs" })}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SummaryMetric({ value, label }) {
  return (
    <Card variant="outlined" sx={{ bgcolor: "background.default" }}>
      <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
        <Typography variant="h6">{value}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function getClubName(windowItem) {
  return windowItem.establishment?.public_name || String(windowItem.establishment_id);
}

function getCourtName(windowItem, intl) {
  return windowItem.field?.name || intl.formatMessage({ id: "scheduling.availability.allCourts" });
}

export function groupAvailabilityWindows(windows) {
  const groups = new Map();

  [...windows]
    .sort((first, second) => first.date.localeCompare(second.date))
    .forEach((windowItem) => {
      const key = [
        windowItem.establishment_id,
        windowItem.field_id || "all",
        windowItem.start_time,
        windowItem.end_time,
      ].join(":");
      if (!groups.has(key)) groups.set(key, { key, windows: [] });
      groups.get(key).windows.push(windowItem);
    });

  return [...groups.values()];
}

function getAvailableCourtIds(windows, fields) {
  const courtIds = new Set();
  windows.forEach((windowItem) => {
    if (windowItem.field_id) {
      courtIds.add(String(windowItem.field_id));
      return;
    }
    fields
      .filter((field) => String(field.establishment_id) === String(windowItem.establishment_id))
      .forEach((field) => courtIds.add(String(field.id)));
  });
  return courtIds;
}

export function summarizeClubs(windows, fields = []) {
  const clubs = new Map();

  windows.forEach((windowItem) => {
    const key = String(windowItem.establishment_id);
    if (!clubs.has(key)) {
      clubs.set(key, {
        key,
        name: getClubName(windowItem),
        windows: [],
      });
    }
    clubs.get(key).windows.push(windowItem);
  });

  return [...clubs.values()].map((club) => ({
    key: club.key,
    name: club.name,
    windowCount: club.windows.length,
    dateCount: new Set(club.windows.map((windowItem) => windowItem.date.slice(0, 10))).size,
    allCourts: club.windows.some((windowItem) => !windowItem.field_id),
    courtCount: getAvailableCourtIds(club.windows, fields).size,
    specificCourtCount: new Set(
      club.windows.filter((windowItem) => windowItem.field_id).map((windowItem) => windowItem.field_id),
    ).size,
  }));
}

export default AvailabilityWindowsSection;
